import { type MouseEvent, useEffect, useState } from 'react';
import { vars } from '@etg/wings';
import { css } from '@eti/styles';
import type { DayEventHandler } from 'react-day-picker';
import { DayPicker, UI } from 'react-day-picker';
import { useDirection, useProperty, useSiteContext } from '@eti/providers';
import type { BestPricePerDay } from '@eti/schema-types';
import 'react-day-picker/src/style.css';
import { startOfMonth } from 'date-fns';
import {
  calculateDayRange,
  getOneYearFromToday,
  isDisabled,
  isInRange,
  isSelected,
} from '../../utils/calendarDateUtils';
import type { Locale } from '../../utils/calendarLocales';
import { formatWeekdayName, getLocale } from '../../utils/calendarLocales';
import { getFirstAndLastSelectableDateButtons } from '../../utils/getFirstAndLastSelectableDateButtons';
import type { DEPARTURE_DATE, RETURN_DATE } from '../../constants/formFields';
import CustomDayWithPriceIndicator from './CustomDayWithPriceIndicator';
import DaysHeader from './DaysHeader';
import { calendarStyles } from './calendarStyles';

const calendarContainerStyle = css`
  margin-inline: -12px;
`;

const mobileStyles = {
  ...calendarStyles,
  [UI.MonthCaption]: css`
    display: flex;
    height: auto;
  `,
  [UI.Month]: css`
    padding: 1rem 0;

    &:not(:last-child) {
      border-block-end: 0.0625rem solid ${vars.colors.divider};
    }
  `,
};

export interface MobileCalendarProps {
  bestPricesPerDay?: BestPricePerDay[];
  currentField?: typeof DEPARTURE_DATE | typeof RETURN_DATE | null;
  daysFromTodayForValidDepartureDate?: number;
  id?: string;
  loading?: boolean;
  onDepartureDateChange?: (day: string) => void;
  onReturnDateChange?: (day: string) => void;
  onDepartureNewDateChange?: (day: Date) => void;
  onReturnNewDateChange?: (day: Date) => void;
  previousBoundDepartureDate?: Date;
  range?: { departureDate?: Date; returnDate?: Date };
  selectedTripType?: string;
}

const MobileCalendar = ({
  bestPricesPerDay,
  currentField,
  daysFromTodayForValidDepartureDate,
  id,
  loading = false,
  onDepartureDateChange,
  onReturnDateChange,
  onDepartureNewDateChange,
  onReturnNewDateChange,
  previousBoundDepartureDate,
  range = {},
  selectedTripType,
}: MobileCalendarProps) => {
  const { direction } = useDirection();

  const { language } = useSiteContext();

  const { p } = useProperty();

  const isPriceCalendarEnabled = p('IbeClient.Price.Calendar.Enabled');

  const [locale, setLocale] = useState<Locale>();

  useEffect(() => {
    const loadLocal = async () => {
      if (language?.locale) {
        const tempLocale = await getLocale(language.locale);
        setLocale(tempLocale);
      }
    };
    loadLocal();
  }, [language?.locale]);

  useEffect(() => {
    if (!currentField) {
      return;
    }

    const dateButtons = getFirstAndLastSelectableDateButtons(range);

    dateButtons[currentField]?.focus();
  }, [currentField, range]);

  const handleDayClick: DayEventHandler<MouseEvent> = (day, { disabled }) => {
    if (!disabled) {
      if (onDepartureNewDateChange && onReturnNewDateChange) {
        calculateDayRange(
          currentField,
          range.departureDate,
          range.returnDate,
          day,
          onDepartureNewDateChange,
          onReturnNewDateChange,
        );
      }
      if (onDepartureDateChange && onReturnDateChange) {
        calculateDayRange(
          currentField,
          range.departureDate,
          range.returnDate,
          day,
          onDepartureDateChange,
          onReturnDateChange,
        );
      }
    }
  };

  return (
    <div className={calendarContainerStyle} id={id}>
      <DaysHeader locale={locale} />
      <DayPicker
        classNames={mobileStyles}
        components={{
          ...(isPriceCalendarEnabled
            ? {
                DayButton: ({ day, modifiers, ...buttonProps }) => (
                  <CustomDayWithPriceIndicator
                    date={day.date}
                    loading={loading}
                    modifiers={modifiers}
                    priceCalendarData={bestPricesPerDay ?? undefined}
                    {...buttonProps}
                  />
                ),
              }
            : {}),
          Weekdays: () => null,
        }}
        dir={direction}
        endMonth={getOneYearFromToday()}
        formatters={{ formatWeekdayName }}
        hideNavigation
        locale={locale}
        modifiers={{
          selected: (day) => isSelected(day, range.departureDate, range.returnDate),
          disabled: (day) =>
            isDisabled(
              day,
              daysFromTodayForValidDepartureDate,
              previousBoundDepartureDate,
              selectedTripType,
            ),
          inRange: (day) => isInRange(day, range.departureDate, range.returnDate, {}),
        }}
        modifiersClassNames={{ inRange: 'rdp-in-range' }}
        modifiersStyles={{
          disabled: { opacity: 1 },
          today: { color: vars.colors.text.primary },
        }}
        numberOfMonths={13}
        onDayClick={handleDayClick}
        startMonth={startOfMonth(new Date())}
      />
    </div>
  );
};

export default MobileCalendar;
